import React, { useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import axiosInstance from '../../../utils/axiosInstance'
import FormErrorsModal from '../../../components/FormErrorsModal'
import { fixEmailLogos } from '../../../utils/emailPreview'
import useApplicantPrivacy from '../../../hooks/useApplicantPrivacy'
import { ApplicantIdentity } from '../../../utils/applicantPrivacy'

export interface LeadEmailHistoryEntry {
  id: string
  type: string
  status: string
  createdAt: string
  email?: string | null
  subject?: string | null
  error?: string | null
}

const EMAIL_TYPE_LABELS: Record<string, string> = {
  askForCV: 'CV request',
  followUp: 'CV follow-up',
  rejection: 'Rejection',
  newLeadNotifForRecruiter: 'New lead notification',
  newLeadNotifForApplicant: 'Applicant confirmation',
  statusUpdate: 'Status update',
  custom: 'Custom',
  other: 'Other',
}

interface LeadEmailHistoryProps {
  leadId: string
  emails: LeadEmailHistoryEntry[]
  // The lead's own contact data, masked out of recipients and bodies for internal users.
  applicant?: ApplicantIdentity
}

const LeadEmailHistory: React.FC<LeadEmailHistoryProps> = ({ leadId, emails, applicant = {} }) => {
  const { maskText, maskHtml } = useApplicantPrivacy()
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [openEmail, setOpenEmail] = useState<{ subject: string; body: string } | null>(null)
  const [errors, setErrors] = useState<string[]>([])

  // The leads list leaves bodies out, so the full email is loaded when it is opened.
  const handleView = async (emailId: string) => {
    setLoadingId(emailId)
    try {
      const { data } = await axiosInstance.get(`/lead/${leadId}/emails/${emailId}`)
      setOpenEmail({
        subject: maskText(data.subject, applicant),
        body: maskHtml(fixEmailLogos(data.body || ''), applicant),
      })
    } catch (error: any) {
      setErrors([error?.response?.data?.message || 'Failed to load email'])
    } finally {
      setLoadingId(null)
    }
  }

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Email History{emails.length > 0 ? ` (${emails.length})` : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {emails.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No emails sent for this lead yet
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sent</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(email.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{EMAIL_TYPE_LABELS[email.type] || email.type}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>
                        {email.email ? maskText(email.email, applicant) : '-'}
                      </TableCell>
                      <TableCell>
                        {email.status === 'failed' ? (
                          <Tooltip title={email.error || ''}>
                            <Chip label="Failed" color="error" size="small" />
                          </Tooltip>
                        ) : (
                          // "Sent" means handed to the mail queue, not confirmed delivered.
                          <Chip label="Sent" color="success" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => handleView(email.id)}
                          disabled={loadingId !== null}
                        >
                          {loadingId === email.id ? 'Loading...' : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={!!openEmail} onClose={() => setOpenEmail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="span">
            {openEmail?.subject || 'Email Content'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpenEmail(null)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ border: '1px solid #ddd', borderRadius: '4px', bgcolor: 'white' }}>
            {/* sandbox: the body contains applicant-submitted text, and an unsandboxed
                srcDoc iframe would run any script in it with the app's origin. */}
            <iframe
              sandbox=""
              srcDoc={openEmail?.body}
              style={{ width: '100%', minHeight: '500px', border: 'none' }}
              title="Email Content"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmail(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <FormErrorsModal
        errorsModalOpen={errors.length > 0}
        formikErrors={errors}
        setErrorsModalOpen={() => setErrors([])}
      />
    </>
  )
}

export default LeadEmailHistory
